/**
 * How a catalog metric's value is rendered — the ONE formatter set the grid cards and the table
 * cells both read.
 *
 * Formatting lives here rather than in the shared catalog because it needs `Intl` and the app's own
 * conventions, and neither belongs in a package the backend imports. The catalog says what a metric
 * IS and what unit it is carried in; this file says what that unit looks like. The split is what
 * lets a card chip and a table cell be the same string by construction instead of by discipline —
 * before it, the table carried its own `percent`/`moneyOrDash`/`signedPercent` helpers and the cards
 * carried theirs.
 *
 * **Withholding is a formatting concern, and it is not the same as zero.** Every formatter here
 * renders an em dash for `null` and `undefined` and never coerces to `0`. A `0%` asserts a measured
 * failure — the creative was shown and nobody clicked — while an em dash reports an absent
 * measurement, and the two license opposite actions. The rule is stated once, in `DASH`, so a new
 * unit cannot quietly opt out of it.
 */
import type { ICreativeMetricEntry, ICreativeMetricUnit } from 'shared'

import { formatSpend } from '@/features/creative-intelligence/utils/creative-intelligence-format'

/** What every metric renders when it has no value. Never `0`, `0%` or `0.00x`. */
export const DASH = '—'

/** A row as a formatter sees it: an arbitrary bag of metric fields, read by name. */
export type IMetricValueRow = Record<string, unknown>

const isBlank = (value: unknown): value is null | undefined => value === null || value === undefined

/** Compact counts — `12.4K` rather than `12,431`, so a column of them stays scannable. */
const compact = (n: number) =>
    new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)

/** A finite number, or `null` for anything that cannot be one. `NaN`/`Infinity` are not values. */
function finite(value: unknown): number | null {
    if (isBlank(value)) return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

/**
 * Decimal places for a rate, by unit. Two for a plain rate because an ad-level conversion rate is
 * routinely under 1% and `0%` would erase the difference between a good creative and a dead one;
 * one for a signed change, where the direction and the order of magnitude are the whole message.
 */
const RATE_DECIMALS: Partial<Record<ICreativeMetricUnit, number>> = { rate: 2, signedRate: 1 }

type IUnitFormatter = (value: unknown, currency: string, unit: ICreativeMetricUnit) => string

/**
 * One count formatter, exported so a column whose identity is per-account formats identically to
 * every catalog count beside it. Without it a custom-conversion column reads `1,284` while the
 * Purchases column next to it reads `1.3K`.
 */
export function formatCountValue(value: unknown): string {
    return UNIT_FORMATTERS.count(value, '', 'count')
}

const UNIT_FORMATTERS: Record<ICreativeMetricUnit, IUnitFormatter> = {
    // Money crosses the wire as an integer-micros STRING. Parsed here and nowhere else, so no
    // surface can round-trip it through a float and lose cents.
    currency: (value, currency) => (isBlank(value) ? DASH : formatSpend(Number(value), currency)),
    rate: (value, _currency, unit) => {
        const n = finite(value)
        return n === null ? DASH : `${(n * 100).toFixed(RATE_DECIMALS[unit] ?? 2)}%`
    },
    // Signed, because the direction IS the information — a bare "18%" hides whether it rose or fell.
    signedRate: (value, _currency, unit) => {
        const n = finite(value)
        if (n === null) return DASH
        return `${n > 0 ? '+' : ''}${(n * 100).toFixed(RATE_DECIMALS[unit] ?? 1)}%`
    },
    multiple: (value) => {
        const n = finite(value)
        return n === null ? DASH : `${n.toFixed(2)}x`
    },
    // No `x` suffix: frequency is impressions per person, and "2.30x" reads as a multiplier on
    // something the reader would then look for.
    ratio: (value) => {
        const n = finite(value)
        return n === null ? DASH : n.toFixed(2)
    },
    count: (value) => {
        const n = finite(value)
        return n === null ? DASH : compact(n)
    },
    days: (value) => {
        const n = finite(value)
        return n === null ? DASH : String(Math.round(n))
    },
    seconds: (value) => {
        const n = finite(value)
        return n === null ? DASH : `${n.toFixed(1)}s`
    },
    date: (value) => {
        if (isBlank(value) || value === '') return DASH
        const date = new Date(String(value))
        return Number.isNaN(date.getTime())
            ? DASH
            : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    status: (value) => (isBlank(value) || value === '' ? DASH : String(value)),
}

/**
 * The row property a metric reads. `field` exists for the entries whose warehouse name differs from
 * their catalog key (`purchaseValue` → `revenueMicros`), which is most of the money ones.
 */
export function metricField(entry: ICreativeMetricEntry): string {
    return entry.field ?? entry.key
}

/**
 * Read a possibly-NESTED field off a row, by dot path.
 *
 * The event metrics live under `eventMetrics.<key>.count`, because an account that does not fire an
 * event carries no entry at all — which is what keeps fifteen null columns off a lead-gen
 * advertiser's response and makes the surface's em dash mean "not measured here". A missing segment
 * yields `undefined`, which every formatter already renders as an em dash.
 */
export function readMetricPath(row: Record<string, unknown>, path: string): unknown {
    if (!path.includes('.')) return row[path]
    let value: unknown = row
    for (const segment of path.split('.')) {
        if (value === null || value === undefined || typeof value !== 'object') return undefined
        value = (value as Record<string, unknown>)[segment]
    }
    return value
}

/**
 * A metric computed on the client from fields the row already carries, rather than read from one.
 *
 * These exist because the operands are on the wire and the quotient is not — sending a derived
 * value the client can compute would widen every response for no new information.
 */
export type IMetricValueOverrides = Readonly<Record<string, (row: IMetricValueRow) => unknown>>

/**
 * One metric's raw value on a row, before formatting.
 *
 * A computed override wins over the row's own field: a `derived` entry has no field to read, and an
 * entry that has both is one the client can compute more precisely than the wire carries.
 */
export function metricValue(
    entry: ICreativeMetricEntry,
    row: IMetricValueRow,
    overrides?: IMetricValueOverrides
): unknown {
    const compute = overrides?.[entry.key]
    return compute ? compute(row) : readMetricPath(row, metricField(entry))
}

/**
 * One metric's display string for one row.
 *
 * Per-key overrides handle the two values that are not a function of their unit alone: a cost per
 * result is WITHHELD as the word "Mixed" when the creative spans differing objectives (an em dash
 * there would read as "not measured" rather than "not comparable"), and a delivery status collapses
 * several member ads into one phrase. Both are meaning rather than formatting, which is why they are
 * named here rather than folded into a unit.
 */
export function formatMetricValue(
    entry: ICreativeMetricEntry,
    row: IMetricValueRow,
    currency: string,
    overrides?: Readonly<Record<string, (row: IMetricValueRow, currency: string) => string>>,
    values?: IMetricValueOverrides
): string {
    const override = overrides?.[entry.key]
    if (override) return override(row, currency)
    return UNIT_FORMATTERS[entry.unit](metricValue(entry, row, values), currency, entry.unit)
}
